import { checkinPhotoUrl } from '@awwyeah/shared';
import type { AttemptOutcome } from './queue';
import { RetryQueue } from './queue';
import type { AnnounceOutboxRecord, OutboxStore, PhotoOutboxRecord } from './store';

/**
 * The check-in outbox: everything a post owes the server after its metadata has already
 * gone into Yjs.
 *
 * Ordering is a design rule, not an implementation detail — *"text and place sync before
 * photos."* That falls out of the architecture rather than being enforced anywhere: the
 * post's text and place are a Yjs write, which is local, instant and replayed by the sync
 * layer the moment there is a socket; the photo is bytes in this queue, which can take as
 * long as it takes. A member with no signal sees their own post immediately, marked
 * "Saved here · will post when there's signal", and the family sees the words as soon as
 * the phone finds a network — the photo lands behind them.
 *
 * Two queues share the machinery:
 *  - **photos** → `PUT /api/checkin/photo/:id`, then flip `photoRef.state` to `uploaded`.
 *  - **announcements** → `POST /api/checkin/announce`, the words for the push notification
 *    (which the server still won't send until the Yjs update lands — see the server
 *    module's `notify.ts`).
 */

export interface CheckinOutboxOptions {
  photos: OutboxStore<PhotoOutboxRecord>;
  announcements: OutboxStore<AnnounceOutboxRecord>;
  /** Called when a photo's bytes are safely on the server — flips Yjs `photoRef.state`. */
  onPhotoUploaded: (record: PhotoOutboxRecord) => void;
  /** Called when an upload starts, so the UI can show `uploading` rather than `local`. */
  onPhotoUploading?: (record: PhotoOutboxRecord) => void;
  fetchImpl?: typeof fetch;
  now?: () => number;
  onChange?: () => void;
  baseDelayMs?: number;
  maxDelayMs?: number;
  setTimeoutImpl?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeoutImpl?: (handle: ReturnType<typeof setTimeout>) => void;
}

/**
 * Which HTTP statuses are worth another try.
 *
 * A 400/413/415 means the server has looked at these exact bytes and said no; the bytes
 * cannot change (the id is their hash), so a retry is guaranteed to fail identically and
 * the record is dropped. Everything else — including 401, which is just a session that
 * needs refreshing, and 5xx — is transient.
 */
export function outcomeForStatus(status: number): AttemptOutcome {
  if (status >= 200 && status < 300) return 'done';
  if (status === 400 || status === 413 || status === 415) return 'drop';
  return 'retry';
}

export class CheckinOutbox {
  private readonly photoQueue: RetryQueue<PhotoOutboxRecord>;
  private readonly announceQueue: RetryQueue<AnnounceOutboxRecord>;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private onlineListener: (() => void) | null = null;

  constructor(private readonly options: CheckinOutboxOptions) {
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
    this.now = options.now ?? ((): number => Date.now());

    const shared = {
      ...(options.now ? { now: options.now } : {}),
      ...(options.onChange ? { onChange: options.onChange } : {}),
      ...(options.baseDelayMs !== undefined ? { baseDelayMs: options.baseDelayMs } : {}),
      ...(options.maxDelayMs !== undefined ? { maxDelayMs: options.maxDelayMs } : {}),
      ...(options.setTimeoutImpl ? { setTimeoutImpl: options.setTimeoutImpl } : {}),
      ...(options.clearTimeoutImpl ? { clearTimeoutImpl: options.clearTimeoutImpl } : {}),
    };

    this.photoQueue = new RetryQueue<PhotoOutboxRecord>({
      ...shared,
      store: options.photos,
      attempt: (record) => this.uploadPhoto(record),
    });

    this.announceQueue = new RetryQueue<AnnounceOutboxRecord>({
      ...shared,
      store: options.announcements,
      attempt: (record) => this.sendAnnouncement(record),
    });
  }

  /**
   * Scans both queues and starts working. Called once on boot — that scan is what makes a
   * photo captured before a crash still arrive afterwards.
   */
  start(): void {
    this.photoQueue.start();
    this.announceQueue.start();
  }

  stop(): void {
    this.photoQueue.stop();
    this.announceQueue.stop();
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
  }

  /** Retry immediately when the browser says the network is back, instead of waiting out
   * a backoff that was earned while there was no point trying. */
  listenForOnline(): void {
    if (typeof window === 'undefined' || this.onlineListener) return;
    this.onlineListener = (): void => void this.flush();
    window.addEventListener('online', this.onlineListener);
  }

  async flush(): Promise<void> {
    await Promise.all([this.photoQueue.drain(), this.announceQueue.drain()]);
  }

  async queuePhoto(
    record: Omit<PhotoOutboxRecord, 'kind' | 'attempts' | 'nextAttemptAt' | 'createdAt'>,
  ): Promise<void> {
    await this.photoQueue.enqueue({ ...record, kind: 'photo', createdAt: this.now() });
  }

  async queueAnnouncement(
    record: Omit<AnnounceOutboxRecord, 'kind' | 'attempts' | 'nextAttemptAt' | 'createdAt' | 'id'>,
  ): Promise<void> {
    await this.announceQueue.enqueue({
      ...record,
      id: record.checkinId,
      kind: 'announce',
      createdAt: this.now(),
    });
  }

  /** Photos still waiting — the feed renders these from the local blob, marked. */
  async pendingPhotos(): Promise<PhotoOutboxRecord[]> {
    return this.photoQueue.pending();
  }

  private async uploadPhoto(record: PhotoOutboxRecord): Promise<AttemptOutcome> {
    this.options.onPhotoUploading?.(record);

    const response = await this.fetchImpl(checkinPhotoUrl(record.id), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': record.contentType },
      body: record.blob,
    });

    const outcome = outcomeForStatus(response.status);
    if (outcome === 'done') this.options.onPhotoUploaded(record);
    if (outcome === 'drop') {
      console.error(
        `[checkin] server refused photo ${record.id.slice(0, 12)}… (${response.status}); ` +
          'it stays on this device only',
      );
    }
    return outcome;
  }

  private async sendAnnouncement(record: AnnounceOutboxRecord): Promise<AttemptOutcome> {
    const response = await this.fetchImpl('/api/checkin/announce', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        checkinId: record.checkinId,
        ...(record.text ? { text: record.text } : {}),
        ...(record.placeName ? { placeName: record.placeName } : {}),
        ...(record.photoId ? { photoId: record.photoId } : {}),
      }),
    });
    return outcomeForStatus(response.status);
  }
}
